/**
 * Smarty Drop, visto desde el PRODUCTO.
 *
 * POR QUÉ EXISTE (2026-08-22): el precio mayorista vive en la publicación (`drop_catalogo`) y no en
 * el producto — es el precio de UN canal, y el mismo producto puede valer distinto en cada uno. Es
 * como lo resuelven las plataformas del rubro: catálogos con listas de precios, no una columna más
 * por canal. Agregarle `precio_drop` a `products` abriría la puerta a `precio_distribuidor`,
 * `precio_mayorista_b`… y el día que dos proveedores tengan condiciones distintas, no entra.
 *
 * Pero el lugar donde una persona QUIERE tocar ese precio es la ficha del producto. Eso es interfaz,
 * no modelo de datos: este módulo es el puente. Se lee y se escribe la publicación desde el editor,
 * sin mover el precio de canal al producto.
 */
import { supabase } from '@/lib/supabase';

/** El proveedor que es este tenant, si lo es. */
export interface ProveedorDrop {
  id: string;
  nombre: string;
  activo: boolean;
  /**
   * ⭐ LO QUE SE VE EN EL CÍRCULO DE LA BANDA DE PROVEEDORES. La columna existía desde que nació la
   * tabla y la banda ya sabía pintarla; lo que faltaba era una pantalla para cargarla, así que todos
   * los proveedores se veían como un círculo de color con su inicial (founder, 23-sep-2026).
   */
  logoUrl: string | null;
  /*
   * 🪤 LA AGENCIA DE ORIGEN NO ESTÁ ACÁ (20261102190000): es la del ALMACÉN (`drop_almacenes.agencia_origen_id`, se
   *    elige en «Tu almacén»). La ficha de proveedor tenía su propia copia y el 2-nov cada una dijo una agencia distinta.
   */
  /** Lo que el revendedor lee antes de pedir: plazos, mínimos, qué pasa si algo falla. */
  condiciones: string | null;
  /** A dónde llega. Texto libre porque cada almacén lo define distinto. */
  cobertura: string | null;
  /** El WhatsApp al que el revendedor escribe si algo se traba. */
  contactoWa: string | null;
  /** De qué almacén sale su mercadería (20261023143000). Null: el suyo. */
  almacenPreferidoId: string | null;
}

/** Lo que este producto publica al por mayor, si publica algo. */
export interface PublicacionDrop {
  catalogoId: string;
  precioMayorista: number;
  /** Lo que el proveedor le paga al almacén por unidad despachada (marca blanca, 23-oct); null = no se liquida. */
  costoDelAlmacen: number | null;
  /** Lo que el proveedor le paga al almacén por unidad despachada (marca blanca, 23-oct); null = no se liquida. */
  /** `null` = sigue el precio de venta del producto, en vivo. Es el modo por defecto. */
  precioSugerido: number | null;
  activo: boolean;
}

/**
 * ¿Este tenant vende al por mayor en Smarty Drop?
 *
 * Devuelve la ficha aunque el proveedor esté APAGADO: prepararlo con el catálogo en off —cargar
 * costos, poner precios, revisar— es exactamente el momento en que hace falta verlo.
 */
export async function proveedorDelTenant(tenantId: string): Promise<ProveedorDrop | null> {
  const { data, error } = await supabase
    .from('drop_proveedores')
    .select('id, nombre, activo, logo_url, condiciones, cobertura, contacto_wa, almacen_preferido_id')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) throw error;
  return data
    ? {
        id: data.id,
        nombre: data.nombre,
        activo: data.activo,
        logoUrl: data.logo_url ?? null,
        almacenPreferidoId: data.almacen_preferido_id ?? null,
        condiciones: data.condiciones ?? null,
        cobertura: data.cobertura ?? null,
        contactoWa: data.contacto_wa ?? null,
      }
    : null;
}

/**
 * Guarda lo que el almacén configura de sí mismo.
 *
 * POR QUÉ EXISTE. Hasta hoy estos campos se seteaban por SQL —igual que `condiciones` y
 * `cobertura`—, y eso alcanzaba mientras el único proveedor era el nuestro. Deja de
 * alcanzar apenas entra el segundo. (La agencia de origen ya no va acá: es del almacén, 20261102190000.)
 *
 * 🪤 NO HACE FALTA UNA RPC: `drop_proveedores` ya tiene una policy de UPDATE que sólo deja tocar la
 *    fila del propio tenant. Escribir una función `security definer` para esto sería agregar una
 *    puerta al lado de una que ya está bien cerrada.
 */
export async function guardarProveedorDrop(
  proveedorId: string,
  cambios: Partial<Pick<ProveedorDrop,
    'activo' | 'condiciones' | 'cobertura' | 'contactoWa' | 'logoUrl'>>,
): Promise<void> {
  const fila: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (cambios.activo !== undefined) fila.activo = cambios.activo;
  if (cambios.condiciones !== undefined) fila.condiciones = cambios.condiciones || null;
  if (cambios.cobertura !== undefined) fila.cobertura = cambios.cobertura || null;
  if (cambios.contactoWa !== undefined) fila.contacto_wa = cambios.contactoWa || null;
  if (cambios.logoUrl !== undefined) fila.logo_url = cambios.logoUrl || null;

  const { error } = await supabase.from('drop_proveedores').update(fila).eq('id', proveedorId);
  if (error) throw error;
}

/** Lo que este producto publica hoy. `null` = no está en el catálogo mayorista. */
export async function publicacionDelProducto(
  proveedorId: string,
  productId: string,
): Promise<PublicacionDrop | null> {
  const { data, error } = await supabase
    .from('drop_catalogo')
    .select('id, precio_mayorista, precio_sugerido, activo, costo_del_almacen')
    .eq('proveedor_id', proveedorId)
    .eq('product_id', productId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    catalogoId: data.id,
    precioMayorista: Number(data.precio_mayorista),
    precioSugerido: data.precio_sugerido === null ? null : Number(data.precio_sugerido),
    costoDelAlmacen: data.costo_del_almacen == null ? null : Number(data.costo_del_almacen),
    activo: data.activo,
  };
}

/**
 * Publica el producto al catálogo mayorista, o actualiza lo que ya publicaba.
 *
 * `precioSugerido` en `null` es el modo por defecto y el recomendado: la sugerencia SIGUE el precio
 * de venta del producto en vivo, en vez de quedar congelada el día que se publicó. Congelarla es una
 * fuga silenciosa —subís tu precio y el catálogo sigue sugiriendo el viejo para siempre— y además
 * evita sugerirle a un revendedor que venda por debajo del proveedor.
 */
export async function publicarEnDrop(
  proveedorId: string,
  productId: string,
  precioMayorista: number,
  precioSugerido: number | null,
  costoDelAlmacen: number | null = null,
): Promise<void> {
  /*
   * 🚨 YA PUBLICADO SE ACTUALIZA, NO SE «UPSERTEA» (founder, 16-oct-2026: «quiero cambiar el precio del Shilajit
   *    Ultra de Suplemax de 28 a 25 y me sale "No se pudo guardar · Error desconocido"»).
   *
   *    Con `upsert`, Postgres arma PRIMERO la fila como nueva —aunque ya exista— y corre sus disparadores de alta.
   *    Esa fila no traía `almacen_id`: `trg_drop_catalogo_almacen_por_defecto` le ponía el almacén del propio
   *    proveedor, y `trg_drop_publicar_exige_origen` la frenaba porque Suplemax no tiene agencia de origen… aunque
   *    la fila de verdad está en el almacén de Vitalex, que sí la tiene. Cambiar un precio quedaba imposible para
   *    todo proveedor con el stock en un almacén ajeno. Ahora: si ya está publicado, un `update` que no toca el
   *    almacén; si no, el alta de siempre.
   */
  const ahora = new Date().toISOString();
  const { data: existente, error: errBuscar } = await supabase
    .from('drop_catalogo')
    .select('id')
    .eq('proveedor_id', proveedorId)
    .eq('product_id', productId)
    .maybeSingle();
  if (errBuscar) throw errBuscar;
  if (existente) {
    const { error } = await supabase
      .from('drop_catalogo')
      .update({ precio_mayorista: precioMayorista, precio_sugerido: precioSugerido, costo_del_almacen: costoDelAlmacen, activo: true, updated_at: ahora })
      .eq('id', (existente as { id: string }).id);
    if (error) throw error;
    return;
  }
  const { error } = await supabase
    .from('drop_catalogo')
    .insert({
      proveedor_id: proveedorId,
      product_id: productId,
      precio_mayorista: precioMayorista,
      precio_sugerido: precioSugerido,
      costo_del_almacen: costoDelAlmacen,
      activo: true,
      updated_at: ahora,
    });
  if (error) throw error;
}

/**
 * Lo saca del catálogo sin borrar la publicación.
 *
 * A propósito no se borra la fila: guarda el precio que se había puesto, así volver a publicarlo no
 * obliga a recordar en cuánto estaba. Y si alguien ya lo importó, el vínculo no se rompe.
 */
export async function sacarDeDrop(catalogoId: string): Promise<void> {
  const { error } = await supabase
    .from('drop_catalogo')
    .update({ activo: false, updated_at: new Date().toISOString() })
    .eq('id', catalogoId);
  if (error) throw error;
}

/** Cuántos revendedores importaron este producto. Es lo que dice si ya no conviene tocarle el precio. */
export async function importacionesDelProducto(catalogoId: string): Promise<number> {
  const { count, error } = await supabase
    .from('drop_producto_importado')
    .select('id', { count: 'exact', head: true })
    .eq('catalogo_id', catalogoId);
  if (error) throw error;
  return count ?? 0;
}

/** Un drop que vende el producto, y lo que le cobra el proveedor. `precioEspecial` nulo = paga el general. */
export interface PrecioDeUnDrop {
  revendedorId: string;
  revendedor: string;
  precioEspecial: number | null;
  /** Unidades que encargó en 30 días: con esto se decide si el beneficio se sostiene. */
  unidades30d: number;
}

/**
 * Los drops que venden un producto y su precio (20261021203000). Sólo el proveedor que ve costos:
 * para cualquier otro la base responde 42501 y la pantalla no muestra la lista.
 */
export async function preciosPorDrop(catalogoId: string): Promise<PrecioDeUnDrop[]> {
  const { data, error } = await supabase.rpc('fn_drop_precios_especiales', { p_catalogo: catalogoId });
  if (error) throw error;
  return (data ?? []).map((f) => ({
    revendedorId: f.revendedor_id,
    revendedor: f.revendedor,
    precioEspecial: f.precio_especial == null ? null : Number(f.precio_especial),
    unidades30d: Number(f.unidades_30d) || 0,
  }));
}

/**
 * Da, cambia o (con `null`) quita el precio especial de un drop. La base exige que sea menor que el
 * general y que lo dé el proveedor; acá no se repite la regla.
 */
export async function guardarPrecioEspecial(catalogoId: string, revendedorId: string, precio: number | null): Promise<void> {
  const { error } = await supabase.rpc('fn_drop_guardar_precio_especial', {
    p_catalogo: catalogoId,
    p_revendedor: revendedorId,
    // El tipo generado no admite nulo en un argumento numeric; la función sí: nulo es «quitar».
    p_precio: precio as number,
  });
  if (error) throw error;
}

/**
 * Vuelve a publicar algo que se había pausado.
 *
 * 🪤 ES EL GEMELO DE `sacarDeDrop`, y existe por la misma razón por la que aquélla no borra la fila:
 *    el precio que se había puesto sigue ahí, así que volver a publicarlo no obliga a recordarlo. Un
 *    interruptor tiene dos posiciones o no es un interruptor.
 */
export async function republicarEnDrop(catalogoId: string): Promise<void> {
  const { error } = await supabase
    .from('drop_catalogo')
    .update({ activo: true, updated_at: new Date().toISOString() })
    .eq('id', catalogoId);
  if (error) throw error;
}

/**
 * Convierte al negocio de la sesión en almacén de Smarty Drop.
 *
 * 🔒 EL TENANT NO VIAJA: lo resuelve `fn_drop_alta_de_proveedor` con la sesión. Si viniera por
 *    parámetro, cualquiera con la consola del navegador podría volverse proveedor de un negocio
 *    ajeno.
 *
 * 🪤 Es idempotente del lado de la base: tocar dos veces el botón devuelve el mismo proveedor en vez
 *    de partir el negocio en dos filas —y partirlo repartiría sus KPIs entre las dos para siempre.
 */
export async function altaDeAlmacen(datos: {
  nombre: string;
  contactoWa?: string;
  cobertura?: string;
  condiciones?: string;
}): Promise<string> {
  const { data, error } = await supabase.rpc('fn_drop_alta_de_proveedor', {
    p_nombre: datos.nombre,
    p_contacto_wa: datos.contactoWa || null,
    p_cobertura: datos.cobertura || null,
    p_condiciones: datos.condiciones || null,
  });
  if (error) throw error;
  return data as string;
}

/**
 * Cuántos productos tiene este almacén en el catálogo, y cuántos publicados.
 *
 * 🪤 SON DOS NÚMEROS Y NO UNO: «tengo productos» y «hay alguien que los puede ver» son pasos
 *    distintos del alta, y el segundo es el que la gente se olvida — los productos entran apagados
 *    a propósito.
 */
export async function cuantosProductosTieneElAlmacen(proveedorId: string): Promise<{ total: number; publicados: number }> {
  const { data, error } = await supabase
    .from('drop_catalogo')
    .select('activo')
    .eq('proveedor_id', proveedorId);
  if (error) return { total: 0, publicados: 0 };   // informativo: no rompe la pantalla
  const filas = (data ?? []) as { activo: boolean }[];
  return { total: filas.length, publicados: filas.filter((f) => f.activo).length };
}
